// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  AccountHistoricalDebt,
  AdvisoryDebtAccount,
} from '@shared/data-access/types/brokerage-types';
import { groupBy } from '@shared/ui-kit';

const dedupeByProviderUuid = <T extends { provider_uuid: string }>(
  items: T[],
): T[] => {
  const seen = new Set<string>();

  return items.filter(item => {
    if (seen.has(item.provider_uuid)) {
      return false;
    }

    seen.add(item.provider_uuid);

    return true;
  });
};

const debtProductSignature = (account: AdvisoryDebtAccount): string => {
  const loanIds = (account.loans ?? [])
    .map(loan => loan.provider_uuid)
    .sort()
    .join(',');
  const creditLineIds = (account.credit_lines ?? [])
    .map(line => line.provider_uuid)
    .sort()
    .join(',');

  return `${loanIds}|${creditLineIds}`;
};

const sumHistoricalDebt = (
  series: AccountHistoricalDebt[],
): AccountHistoricalDebt => {
  if (series.length === 0) {
    return { short_term: [], long_term: [] };
  }

  const length = series[0]!.short_term.length;

  return {
    short_term: Array.from({ length }, (_, index) =>
      series.reduce((sum, item) => sum + (item.short_term[index] ?? 0), 0),
    ),
    long_term: Array.from({ length }, (_, index) =>
      series.reduce((sum, item) => sum + (item.long_term[index] ?? 0), 0),
    ),
  };
};

const mergeInstitutionDebtAccounts = (
  institutionAccounts: AdvisoryDebtAccount[],
): AdvisoryDebtAccount => {
  const [primary] = institutionAccounts;

  const loans = dedupeByProviderUuid(
    institutionAccounts.flatMap(account => account.loans ?? []),
  );
  const credit_lines = dedupeByProviderUuid(
    institutionAccounts.flatMap(account => account.credit_lines ?? []),
  );

  const signatures = institutionAccounts.map(debtProductSignature);
  const sharesSameProducts = signatures.every(
    signature => signature === signatures[0],
  );

  const historical_debt = sharesSameProducts
    ? institutionAccounts.find(account => debtProductSignature(account) !== '|')
        ?.historical_debt ?? primary.historical_debt
    : sumHistoricalDebt(
        institutionAccounts.map(account => account.historical_debt),
      );

  return {
    ...primary,
    loans,
    credit_lines,
    historical_debt,
  };
};

/**
 * Collapses per-IBAN debt payloads so institution-level loans and credit lines are
 * not counted once per selected account.
 */
export const normalizeAdvisoryDebtAccounts = (
  accounts: AdvisoryDebtAccount[] = [],
): AdvisoryDebtAccount[] => {
  if (accounts.length <= 1) {
    const [account] = accounts;

    if (!account) {
      return [];
    }

    return [
      {
        ...account,
        loans: dedupeByProviderUuid(account.loans ?? []),
        credit_lines: dedupeByProviderUuid(account.credit_lines ?? []),
      },
    ];
  }

  const accountsByInstitution = groupBy(accounts, 'institution_id');

  return Array.from(accountsByInstitution.values()).map(
    mergeInstitutionDebtAccounts,
  );
};
